// Bubble.io rich text is stored as BBCode-like markup:
//   [ml]...[/ml]                            — outer wrapper, unwrap
//   [ul]...[/ul] / [ol]...[/ol]             — lists
//   [li indent=0 align=left]...[/li]        — list items (strip attrs)
//   [b]/[i]/[u]                             — inline formatting
//   [color=#xxx]/[size=N]/[h1]/[align=...]  — unknown, strip wrapper
function bubbleToHtml(s) {
  return s
    .replace(/\[ml\b[^\]]*\]/gi, '')
    .replace(/\[\/ml\]/gi, '')
    .replace(/\[ul\b[^\]]*\]/gi, '<ul>')
    .replace(/\[\/ul\]/gi, '</ul>')
    .replace(/\[ol\b[^\]]*\]/gi, '<ol>')
    .replace(/\[\/ol\]/gi, '</ol>')
    .replace(/\[li\b[^\]]*\]/gi, '<li>')
    .replace(/\[\/li\]/gi, '</li>')
    .replace(/\[b\b[^\]]*\]/gi, '<b>')
    .replace(/\[\/b\]/gi, '</b>')
    .replace(/\[i\b[^\]]*\]/gi, '<i>')
    .replace(/\[\/i\]/gi, '</i>')
    .replace(/\[u\b[^\]]*\]/gi, '<u>')
    .replace(/\[\/u\]/gi, '</u>')
    // Strip any other BBCode tag we don't translate (keep inner text).
    .replace(/\[\/?[a-zA-Z][^\]]*\]/g, '')
}

// Convert a legacy stored value to safe HTML so the rich-text editor / view
// renders it correctly. Three input shapes are handled:
//   - HTML (already has tags)        → pass through
//   - Bubble BBCode ([ml]/[ul]/[b]…) → convert tags, preserve surrounding text
//   - Plain text                     → escape + <br> for newlines
export function toHtml(input) {
  if (input == null) return ''
  let s = String(input)
  const hasHtml   = /<[a-z][^>]*>/i.test(s)
  const hasBubble = /\[\/?[a-zA-Z][a-zA-Z0-9]*\b[^\]]*\]/.test(s)
  if (hasBubble) s = bubbleToHtml(s)
  if (hasHtml || hasBubble) {
    return s.replace(/\r?\n/g, '<br>')
  }
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\r?\n/g, '<br>')
}

// Rich text (HTML, Bubble BBCode or plain) → ONE line of plain text, for a summary
// that must not render markup: each <br> / block end becomes " · ", entities decoded.
export function htmlToText(input) {
  if (input == null) return ''
  return toHtml(input)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6])>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, '&')
    .split('\n').map((l) => l.replace(/\s+/g, ' ').trim()).filter(Boolean).join(' · ')
}

// True if the (possibly HTML) value renders to nothing visible.
export function isHtmlEmpty(html) {
  if (!html) return true
  const text = String(html).replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim()
  return text === ''
}

const ALLOW_TAGS = new Set([
  'B', 'STRONG', 'I', 'EM', 'U',
  'UL', 'OL', 'LI',
  'BR', 'P', 'DIV', 'SPAN',
])

// Strip every tag not in the allowlist and every attribute on tags we keep.
// Returns sanitized HTML; safe to feed into dangerouslySetInnerHTML.
export function sanitizeHtml(html) {
  if (!html) return ''
  if (typeof document === 'undefined') return ''
  const tpl = document.createElement('template')
  tpl.innerHTML = String(html)
  const walk = (node) => {
    const children = Array.from(node.childNodes)
    for (const c of children) {
      if (c.nodeType !== 1) continue
      if (!ALLOW_TAGS.has(c.tagName)) {
        while (c.firstChild) c.parentNode.insertBefore(c.firstChild, c)
        c.remove()
        continue
      }
      for (const attr of Array.from(c.attributes)) c.removeAttribute(attr.name)
      walk(c)
    }
  }
  walk(tpl.content)
  return tpl.innerHTML
}
